import { ReactNode, useEffect, useState } from 'react';
import {
  faLink,
  faTag,
  faStars,
  faTable,
  faArrowRight,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { clsx } from 'clsx';
import { useLocalStorage } from '@client/hooks/use-local-storage';
import { PlainModal } from '@design-system/components/modal/modal';
import { fullSize } from '@design-system/components/modal/modal.css';
import { Typography } from '@design-system/components/typography/typography';
import { Button } from '@design-system/components/button/button';
import { HELP_LINKS } from '@client/new/common/helpLinks';
import * as styles from './OtpMigrationWelcomeModal.css';

const SEEN_KEY = 'otpMigrationWelcomeModalSeen';

type Step = {
  title: string;
  description: string;
  cards: Array<{ icon: IconProp; title: string; description: ReactNode }>;
};

const STEPS: [Step, Step] = [
  {
    title: 'Your OTP Programs are now Offers',
    description:
      "Since Playbook is evolving and allowing for different business models, we've automatically migrated your OTP programs to Offers. Your links, landing pages, and pricing are all migrated — nothing is broken.",
    cards: [
      {
        icon: faLink,
        title: 'All your links still work',
        description:
          "Every landing page and checkout link redirects correctly. No need to update anything you've already shared.",
      },
      {
        icon: faTag,
        title: 'Pricing and links for your OTP Programs live in Offers now',
        description:
          'Going forward, grab your purchase links from the Offers tab, not Programs. Programs are primarily for building content.',
      },
    ],
  },
  {
    title: 'Why did this change?',
    description:
      "Think of what you sell as packages. You can change what's in a package or the price of that package in Offers.",
    cards: [
      {
        icon: faStars,
        title: 'More flexibility with pricing and packaging',
        description:
          'With OTP Offers, you can bundle multiple Programs into a single Offer, set one price, and share one link. You can also create multiple Offers for the same Program at different price points, useful for limited-time promotions.',
      },
      {
        icon: faTable,
        title: 'One place to manage your business in the near future',
        description: (
          <>
            Now everything you sell, whether it&apos;s a one-time purchase or a
            subscription, lives in Offers.
            <br />
            <br />
            This is part of a bigger change coming where going forward, creators
            build content in one place then pricing, packaging, landing pages,
            and sharing links all live on the Offers tab. The two are linked —
            Offers wrap Programs.
          </>
        ),
      },
    ],
  },
];

type OtpMigrationWelcomeModalProps = {
  visible: boolean;
  learnMoreHref?: string;
};

export const OtpMigrationWelcomeModal = ({
  visible,
  learnMoreHref = HELP_LINKS.otpOffersMigration,
}: OtpMigrationWelcomeModalProps) => {
  const [seen, setSeen] = useLocalStorage(SEEN_KEY, false);
  const [isOpen, setIsOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    if (visible && !seen) {
      setStepIndex(0);
      setIsOpen(true);
    }
  }, [visible, seen]);

  // Closing via the X or backdrop does NOT mark the modal as seen, so it
  // shows again on the next reload. Only "Ok, got it!" persists the flag.
  const close = () => setIsOpen(false);

  const confirm = () => {
    setSeen(true);
    setIsOpen(false);
  };

  const isLastStep = stepIndex === STEPS.length - 1;
  const step = STEPS[stepIndex];

  return (
    <PlainModal
      isOpen={isOpen}
      onClose={close}
      modalClassName={clsx(fullSize, 'w-full md:w-[742px]')}
    >
      <div className={styles.content}>
        {isLastStep && (
          <button
            type="button"
            className={styles.closeButton}
            onClick={close}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )}
        <div className={styles.textGroup}>
          <div className={styles.tag}>
            <Typography variant="body" size="medium" color="P-100">
              Your account was updated
            </Typography>
          </div>
          <Typography variant="headline" size="large" color="B-100">
            {step.title}
          </Typography>
          <Typography
            variant="body"
            size="large"
            fontWeight="normal"
            color="N-100"
          >
            {step.description}
          </Typography>
        </div>
        <div className={styles.cards}>
          {step.cards.map((card) => (
            <div key={card.title} className={styles.card}>
              <div className={styles.cardHeader}>
                <FontAwesomeIcon icon={card.icon} className={styles.cardIcon} />
                <Typography variant="body" size="large" color="B-100">
                  {card.title}
                </Typography>
              </div>
              <Typography
                variant="body"
                size="medium"
                fontWeight="normal"
                color="N-90"
              >
                {card.description}
              </Typography>
            </div>
          ))}
        </div>
        <div className={styles.buttonRow}>
          {isLastStep ? (
            <>
              <a
                href={learnMoreHref}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.learnMore}
              >
                <Typography variant="body" size="medium" color="P-80">
                  Learn More
                </Typography>
              </a>
              <Button
                color="neutral"
                className={styles.actionButton}
                onClick={confirm}
              >
                Ok, got it!
              </Button>
            </>
          ) : (
            <Button
              color="neutral"
              rightIcon={faArrowRight}
              className={styles.actionButton}
              onClick={() => setStepIndex((i) => i + 1)}
            >
              Next
            </Button>
          )}
        </div>
      </div>
    </PlainModal>
  );
};
